import { NgModule } from '@angular/core';
import { Routes, RouterModule, ExtraOptions } from '@angular/router';

// --- Error Handling ---
import { NotFoundComponent } from './core/components/error-handling/not-found/not-found.component';
import { ServerErrorComponent } from './core/components/error-handling/server-error/server-error.component';
import { AccessDeniedComponent } from './core/components/error-handling/access-denied/access-denied.component';

// --- Guards ---
import { AuthGuard } from './core/guards/auth.guard';

// --- Auth Components ---
import { LoginPageComponent } from './core/components/auth/login-page/login-page.component';
import { RegisterPageComponent } from './core/components/auth/register-page/register-page.component';
import { ForgotPasswordPageComponent } from './core/components/auth/forgot-password-page/forgot-password-page.component';
import { ChangepasswordComponent } from './core/components/changepassword/changepassword.component';
import { ConfirmemailComponent } from './core/components/confirmemail/confirmemail.component';

// --- Admin ---
import { AdminPanelComponent } from './core/components/admin/admin-panel/admin-panel.component';
import { AuditsComponent } from './core/components/admin/audits/audits.component';
import { TenantComponent } from './core/components/admin/tenant/tenant.component';
import { UserManagementComponent } from './core/components/admin/user-management/user-management.component';
import { PermissionGroupsComponent } from './core/components/admin/permission-groups/permission-groups.component';
import { EndpointPermissionsComponent } from './core/components/admin/endpoint-permissions/endpoint-permissions.component';
import { RemovedEndpointsComponent } from './core/components/admin/removed-endpoints/removed-endpoints.component';
import { CurrencyManagementComponent } from './core/components/admin/currency-management/currency-management.component';
import { IntegrationsComponent } from './core/components/admin/integrations/integrations.component';
import { LoginGuard } from './core/guards/login.guard';

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'login',
  },
  {
    path: 'login',
    component: LoginPageComponent,
    canActivate: [LoginGuard],
  },
  {
    path: 'register',
    component: RegisterPageComponent,
    canActivate: [LoginGuard],
  },
  { path: 'forgotpassword', component: ForgotPasswordPageComponent },

  // Rotas Legado Auth
  {
    path: 'changePassword/:userEmail/:code',
    component: ChangepasswordComponent,
  },
  { path: 'changePassword', component: ChangepasswordComponent },
  { path: 'confirmEmail/:userEmail/:code', component: ConfirmemailComponent },
  { path: 'confirmemail', component: ConfirmemailComponent },

  // --- Módulos Lazy Loaded ---

  {
    path: 'home',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('src/app/modules/template/template.module').then(
        (m) => m.TemplateModule,
      ),
  },

  // CS PDA
  {
    path: 'pda',
    canActivate: [AuthGuard],
    data: {
      roles: [
        'Admin',
        'PDA40_Admin',
        'PDA Express',
        'manage_vessel',
        'manage_port',
        'manage_terminal',
        'manage_berth',
        'manage_grade',
      ],
    },
    loadChildren: () =>
      import('src/app/modules/pda/pda.module').then(
        (m) => m.PdaModule,
      ),
  },

  // CS DA
  {
    path: 'da',
    canActivate: [AuthGuard],
    data: {
      roles: [
        'Admin',
        'PDA40_Admin',
        'manage_client',
        'manage_voucher',
      ],
    },
    loadChildren: () =>
      import('src/app/modules/da/da.module').then(
        (m) => m.DaModule,
      ),
  },

  // CS Finance
  {
    path: 'finance',
    canActivate: [AuthGuard],
    data: {
      roles: [
        'Admin',
        'PDA40_Admin',
        'PDA Express',
        'manage_vessel',
        'manage_client',
        'manage_voucher',
        'manage_port',
        'manage_terminal',
        'manage_berth',
        'manage_grade',
      ],
    },
    loadChildren: () =>
      import('src/app/modules/finance/finance.module').then(
        (m) => m.FinanceModule,
      ),
  },

  // CS Port Call
  {
    path: 'port-call',
    canActivate: [AuthGuard],
    data: {
      roles: [
        'Admin',
        'PDA40_Admin',
        'PDA Express',
        'manage_vessel',
        'manage_port',
        'manage_terminal',
        'manage_berth',
      ],
    },
    loadChildren: () =>
      import('src/app/modules/port-call/port-call.module').then(
        (m) => m.PortCallModule,
      ),
  },

  {
    path: 'legal',
    canActivate: [AuthGuard],
    // Idealmente faça o mesmo para o Legal, listando as roles Jurídicas
    data: {
      roles: ['Admin', 'Legal', 'LEGAL_BASIC', 'LEGAL_DIRECTOR'],
    },
    loadChildren: () =>
      import('src/app/modules/legal/legal.module').then((m) => m.LegalModule),
  },

  // CS Bill of Lading
  {
    path: 'bill-of-lading',
    canActivate: [AuthGuard],
    data: {
      roles: ['Admin', 'docs_admin', 'docs'],
    },
    loadChildren: () =>
      import('src/app/modules/bill-of-lading/bill-of-lading.module').then(
        (m) => m.BillOfLadingModule,
      ),
  },

  // --- Rotas Administrativas ---
  {
    path: 'admin',
    component: AdminPanelComponent,
    canActivate: [AuthGuard],
    // Admin Panel só entra quem é Admin mesmo
    data: {
      roles: ['Admin', 'TenantAdmin', 'manage_user'],
      groups: ['Admin', 'Managed'],
    },
    children: [
      { path: '', redirectTo: 'users', pathMatch: 'full' },
      { path: 'users', component: UserManagementComponent },
      { path: 'groups', component: PermissionGroupsComponent },
      { path: 'endpoint-permissions', component: EndpointPermissionsComponent },
      {
        path: 'removed-endpoints',
        component: RemovedEndpointsComponent,
      },
      { path: 'tenant', component: TenantComponent },
      { path: 'currencies', component: CurrencyManagementComponent },
      {
        path: 'integrations',
        component: IntegrationsComponent,
      },
      { path: 'audits', component: AuditsComponent },
    ],
  },

  // --- Rotas de Erro ---
  { path: 'access-denied', component: AccessDeniedComponent },
  { path: 'not-found', component: NotFoundComponent },
  { path: 'server-error', component: ServerErrorComponent },

  // IMPORTANTE: Redireciona qualquer rota desconhecida para not-found
  { path: '**', redirectTo: 'not-found', pathMatch: 'full' },
];

const routerOptions: ExtraOptions = {
  useHash: false,
  anchorScrolling: 'enabled',
  scrollPositionRestoration: 'enabled',
};

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
